<template>
  <div>
    <v-overlay
      :value="showHorseAssignList"
      opacity="0.5"
      z-index="10000"
    >
      <v-container>
        <v-card
          light
          :width="650"
        >
          <v-card-title class="third d-flex justify-space-between white--text">
            <v-col cols="11">
              <span>Sélectionnez le cheval pour le participant <strong v-if="selectedParticipant">{{ selectedParticipant.fullname }}</strong></span>
            </v-col>
            <v-col cols="1">
              <v-btn icon color="white" @click="closeOverlay">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-card-title>

          <div v-if="!loading" class="full-width pa-4">
            <HorseTableFilter />
            <v-row no-gutters>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                class="pb-4 pt-0 mt-0 mr-4"
              />
            </v-row>
            <v-row no-gutters>
              <v-data-table
                v-model="selectedContentType"
                height="480"
                show-select
                :items="getAvailableHorses"
                item-key="uuid"
                :search="search"
                :hide-default-footer="true"
                :items-per-page="-1"
                :headers="headers"
                :single-select="true"
                :custom-filter="filter"
                @item-selected="selectItem"
                @click:row="rowClick"
              >
                <template #item.name="{ item }">
                  {{ item.horse_name }}
                </template>
                <template #item.dailyEffort="{ item }">
                  <v-col cols="12" class="light-pink-bg text-center">
                    {{ item.dailyEffort }}
                  </v-col>
                </template>
                <template #item.weeklyEffort="{ item }">
                  <v-col cols="12" class="dark-pink-bg text-center">
                    {{ item.weeklyEffort }}
                  </v-col>
                </template>
              </v-data-table>
            </v-row>
          </div>
          <div v-else class="d-flex justify-center align-center full-height min-height350">
            <Loader />
          </div>
        </v-card>
      </v-container>
    </v-overlay>
  </div>
</template>

<script>
import { ref, computed, inject, watchEffect, provide, onBeforeMount } from '@vue/composition-api';

import { availableHorses, filterHorse, checkHorseDailyEffort } from '@/helpers/dataManager';
import { sortArray } from '@/helpers/utils';
import { updateSessionAmplify } from '@/helpers/amplify';
import Loader from '@/components/common/Loader.vue';

import HorseTableFilter from '@/components/ParticipantHorse/HorseTableFilter.vue';

import store from '@/store';

export default {
  components: { HorseTableFilter, Loader },
  setup () {
    const loading = ref(false);
    const showHorseAssignList = inject('showHorseAssignList');
    const disableAll = inject('disableAll');

    const search = ref(null);
    const horseWithoutParticipant = ref(null);

    const selectedContentType = ref([]);
    const selectedHorseTypes = ref([]);
    const selectedParticipant = inject('selectedParticipant');
    const selectedSession = inject('selectedSession');
    const selectedLevels = ref([]);

    const closeOverlay = () => {
      showHorseAssignList.value = false;
      disableAll.value = true;
      selectedHorseTypes.value = [];
    };

    const headers = computed(() => [
      { text: 'Nom', value: 'name', sortable: false },
      { text: 'Effort quotidien', value: 'dailyEffort', align: 'center', sortable: true },
      { text: 'Effort hebdomadaire', value: 'weeklyEffort', align: 'center', sortable: true }
    ]);

    const getAvailableHorses = computed(() => {
      if (selectedSession.value) {
        const sessionParticipant = selectedSession.value.participants.find(p => p.uuid === selectedParticipant.value.uuid);
        let availableHorsesForSession = availableHorses(selectedSession.value, true, true, true);
        if (sessionParticipant.horse) {
          const participantHorse = store.getters.horse(sessionParticipant.horse);

          if (!availableHorsesForSession.some(h => h.uuid === participantHorse.uuid)) {
            availableHorsesForSession.push(participantHorse);
          }
        }

        (selectedSession.value.horses || []).map(horse => {
          const doesParticipantHaveHorse = (selectedSession.value.participants || []).some(p => p.horse === horse.uuid);

          if (!doesParticipantHaveHorse && !availableHorsesForSession.some(h => h.uuid === horse.uuid)) {
            availableHorsesForSession.push(store.getters.horse(horse.uuid));
          }
        });

        availableHorsesForSession = sortArray(availableHorsesForSession, 'wtitle');

        return filterHorse(availableHorsesForSession, horseWithoutParticipant.value, selectedSession.value, selectedHorseTypes.value, null, selectedLevels.value);
      }
      return [];
    });

    const filter = (_, searchValue, item) => item.horse_name.toLowerCase().includes(searchValue.toLowerCase());

    const addHorse = async selectedItem => {
      // value meaning if isSelect
      const { item, value } = selectedItem;
      const session = selectedSession.value;

      const sessionParticipants = session.participants;
      const sessionParticipantIndex = sessionParticipants.findIndex(participant => participant.uuid === selectedParticipant.value.uuid);

      if (sessionParticipantIndex !== -1) {
        // if horse already on session just add/remove from participant
        const sessionHorseIndex = session.horses.findIndex(horse => horse.uuid === item.uuid);
        const participantWithHorseIndex = sessionParticipants.findIndex(participant => participant.horse === item.uuid);

        if (!value) {
          if (sessionHorseIndex !== -1) {
            session.horses.splice(sessionHorseIndex, 1);

            if (participantWithHorseIndex !== -1) {
              sessionParticipants[participantWithHorseIndex].horse = null;
            }
          }
        } else {
          // horse is Selected
          if (sessionHorseIndex === -1) {
            const horse = store.getters.horse(item.uuid);
            const checkForHorseEffort = await checkHorseDailyEffort([horse], [session]);
            if (Object.keys(checkForHorseEffort).length) {
              closeOverlay();
              store.commit('setAlertEffort', checkForHorseEffort);
              store.commit('setShowMaxEffortModal', true);
              store.commit('setMaxEffortCallback', () => {
                session.horses.push({ wtitle: item.horse_name, uuid: item.uuid });
                sessionParticipants[sessionParticipantIndex].horse = item.uuid;
                session.participants = sessionParticipants;
                updateSessionAmplify(session);
              });
              return;
            }
            session.horses.push({ wtitle: item.horse_name, uuid: item.uuid });
          }
          sessionParticipants[sessionParticipantIndex].horse = item.uuid;
        }
      }

      session.participants = sessionParticipants;

      updateSessionAmplify(session);
      showHorseAssignList.value = false;
      disableAll.value = true;
    };

    const selectItem = async selectedItem => {
      const { item, value } = selectedItem;

      selectedContentType.value = value ? [item] : [];
      addHorse(selectedItem);
    };

    const rowClick = (item, row) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    watchEffect(() => {
      const sessionParticipant = selectedSession.value.participants.find(p => p.uuid === selectedParticipant.value.uuid);
      const participantHorse = store.getters.horse(sessionParticipant.horse);

      selectedContentType.value = [participantHorse];
    });

    onBeforeMount(async () => {
      loading.value = true;
      if (!store.state.isLazyLoaded.horses) {
        await store.dispatch('getHorses');
      }
      loading.value = false;
    });

    provide('horseWithoutParticipant', horseWithoutParticipant);
    provide('selectedHorseTypes', selectedHorseTypes);
    provide('levelsList', selectedLevels);

    return { selectedHorseTypes, horseWithoutParticipant, selectedParticipant, selectedContentType, selectItem, rowClick, showHorseAssignList, closeOverlay, search, disableAll, getAvailableHorses, headers, filter, loading };
  }
};
</script>
